// @ts-check
"use strict"

import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";
import EditReport from "./EditReport"
import PreviewReport from "./PreviewReport";
import { ChatMarkdown } from "../chat-bubble/ChatBubble";
import { DocumentSharingWindow, ReviewRequestWindow } from "../anokye-content/components/anokye-sharing-window";
import { parseFieldsToMarkdown, parseJsonToFields, parseJsonToMarkdown } from "./utils";
import { showErrorMessage, showWarningMessage } from "@minohealth/common/utils/notifiers";
import { getModalityLabel } from "./ReportFormDialog";

import { IoMdClose } from 'react-icons/io'
import { MdEdit } from "react-icons/md";
import { IoMdShareAlt } from "react-icons/io";
import { IoDocumentText, IoEllipsisVertical, IoArrowBack } from "react-icons/io5";
import { FaSync } from "react-icons/fa";
import { ChevronDownIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import "./chat-report.css"
import { useChatContext } from "@/services/chat-provider";

const ReportState = /** @type {const} */({
  LOADING: "loading",
  EDITING: "editing",
  PREVIEWING: "previewing"
})

/** @typedef {typeof ReportState[keyof typeof ReportState]} ReportState */

/**
 * @typedef {Object} ReportSourceRef
 * @property {'ref'} type
 * @property {string} ref
 */

/**
 * @typedef {Object} ReportSourceReport
 * @property {'report'} type
 * @property {ChatReport} report
 */

/**
 * @typedef {Object} ReportSourceStream
 * @property {"stream"} type
 * @property {string} sessionId
 */

/**
 * @typedef {ReportSourceRef | ReportSourceReport | ReportSourceStream | null} ReportSource
 */

/**
 * @typedef {Object} ReportPanelComponent
 * @property {boolean} isPanelOpen
 * @property {(reportParam: ReportSource) => void} openPanel
 * @property {() => void} closePanel
 */

// TODO: use report id instead of index
export default forwardRef(
  /**
   * @param {Object} props
   * @param {() => void} [props.onClosePanel]
   * @param {() => void} [props.onOpenPanel]
   * @param {number} [props.width] - Width percentage for split screen
   * @param {(width: number) => void} [props.onResize] - Resize handler
   * @param {React.Ref<ReportPanelComponent>} ref
   */
  function ChatReportPanel({ onOpenPanel, onClosePanel }, ref) {
    const { useActiveChatSession, useActiveChatSessionReports, useCancelActiveChatStream } = useChatContext()

    const { data: activeChatSession } = useActiveChatSession()
    const { data: activeChatSessionReports } = useActiveChatSessionReports()
    const { mutate: canceltream } = useCancelActiveChatStream()
    const [isPanelOpen, setIsPanelOpen] = useState(false)
    const [reportSource, setReportSource] = useState(null)
    const [isDownloading, setIsDownloading] = useState(false)
    const reportViewStartTime = useRef(null)

    const isStreamingReport = reportSource?.type === "stream"
    const { report, reportData } = useReportData(
      activeChatSession?._id,
      reportSource,
      activeChatSessionReports,
    )

    /** @type {[string, function]} */
    const [reportState, setReportState] = useState(() => {
      if (isStreamingReport) return ReportState.LOADING
      return ReportState.PREVIEWING
    })

    useEffect(() => {
      if (isStreamingReport) setReportState(ReportState.LOADING)
      if (!isStreamingReport && reportState === ReportState.LOADING) {
        setReportState(ReportState.PREVIEWING)
      }
    }, [isStreamingReport, reportState])

    useEffect(() => {
      if (reportData != null) return
      closePanel()
    }, [reportData, isPanelOpen])

    useEffect(() => {
      if (isPanelOpen && reportViewStartTime.current == null) {
        reportViewStartTime.current = Date.now()
        mixpanel.track("View Report", {
          reportId: report?._id,
          patientName: reportData?.patient?.name || reportData?.patientInfo?.name
        })
      }
    }, [isPanelOpen, report?._id, reportData?.patient?.name, reportData?.patientInfo?.name])

    useEffect(() => {
      return () => closePanel()
    }, [activeChatSession?._id])

    /** @param {Object} reportParam - Either { type: 'ref', ref: string } or { type: 'report', report: Object } */
    function openPanel(reportParam) {
      if (isPanelOpen && JSON.stringify(reportParam) === JSON.stringify(reportSource)) return
      setIsPanelOpen(true)
      setReportSource(reportParam)
      onOpenPanel?.()
    }

    function closePanel() {
      if (!isPanelOpen) return
      if (reportViewStartTime.current != null) {
        const viewDuration = Math.round((Date.now() - reportViewStartTime.current) / 1000)
        mixpanel.track("Close Report", {
          reportId: report?._id,
          viewDurationSeconds: viewDuration,
          patientName: reportData?.patient?.name || reportData?.patientInfo?.name
        })
        reportViewStartTime.current = null
      }
      setIsPanelOpen(false)
      setReportSource(null)
      setReportState(ReportState.PREVIEWING)
      onClosePanel?.()
    }

    useImperativeHandle(ref, () => ({
      isPanelOpen,
      openPanel,
      closePanel
    }), [isPanelOpen, reportSource])

    function gotoPreview() {
      setReportState(ReportState.PREVIEWING)
    }

    function gotoEditForm() {
      mixpanel.track("Edit Report", {
        reportId: report?._id,
        patientName: reportData?.patient?.name || reportData?.patientInfo?.name
      })
      setReportState(ReportState.EDITING)
    }

    /** @type {React.RefObject<import("../anokye-content/components/anokye-sharing-window").SharingWindowExternalAPI<ChatReport>>} */
    const sharingWindowRef = useRef(null)
    /** @type {React.RefObject<import("../anokye-content/components/anokye-sharing-window").SharingWindowExternalAPI<any>>} */
    const reviewWindowRef = useRef(null)

    function openSharingWindow() {
      mixpanel.track("Share Report", {
        reportId: report?._id,
        patientName: reportData?.patient?.name || reportData?.patientInfo?.name
      })
      sharingWindowRef.current?.openWindow(report)
    }

    /**
     * Validate that all required report fields are filled
     * @param {any} reportData - The report data to validate
     * @returns {{ isValid: boolean, missingFields: string[] }}
     */
    function validateReportFields(reportData) {
      const missingFields = [];

      const patient = reportData?.patient || reportData?.patientInfo;
      if (!patient?.name || patient.name.trim() === "") missingFields.push("Patient name");
      if (!patient?.id || patient.id.toString().trim() === "") missingFields.push("Patient ID");
      if (patient?.age == null) missingFields.push("Patient age");
      if (!patient?.gender) missingFields.push("Patient gender");

      const facility = reportData?.facility;
      if (!facility?.name || facility.name.trim() === "") missingFields.push("Facility name");
      if (!facility?.reportingDoctor || facility.reportingDoctor.trim() === "") missingFields.push("Reporting doctor");
      if (!facility?.reportDate || facility.reportDate.trim() === "") missingFields.push("Report date");

      return {
        isValid: missingFields.length === 0,
        missingFields
      };
    }

    /**
     * Handle PDF download
     * @param {any} reportData - The report data to download
     * @param {ChatReport} report - The report object
     */
    async function handleDownloadPDF(reportData, report) {
      if (isDownloading) return; // Prevent multiple simultaneous downloads

      const validation = validateReportFields(reportData);
      if (!validation.isValid) {
        const missingFieldsList = validation.missingFields.join(", ");
        showWarningMessage(`Missing: ${missingFieldsList}.`, "Please fill in all required information before downloading");
        gotoEditForm();
        return;
      }

      setIsDownloading(true);
      try {
        const getFileName = () => {
          // Handle both regular reports (patient/facility) and agent reports (patientInfo)
          const patient = reportData?.patient || reportData?.patientInfo;
          if (!patient) return "medical_record.pdf";

          const patientName = patient?.name?.replace(/\s+/g, "_").toLowerCase() || "unknown_patient";
          const age = patient?.age || "unknown_age";
          const date = reportData?.facility?.reportDate
            ? new Date(reportData?.facility.reportDate).toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0];

          return `medical_record_${patientName}_${age}_${date}.pdf`;
        };

        const response = await fetch(new URL("pdf/generate-report", import.meta.env.VITE_SOCKET_URL), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            reportData,
            blobImageUrl: null, // Optional field for PDF generation
            fileName: getFileName()
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Create blob from response
        const blob = await response.blob();

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = getFileName();
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: 'ChatReportPanel - handleDownloadPDF',
            reportId: report?._id || 'undefined',
            patientName: (reportData?.patient?.name || reportData?.patientInfo?.name) || 'undefined',
            patientAge: (reportData?.patient?.age || reportData?.patientInfo?.age) || 'undefined',
            reportDataPresent: !!reportData,
            isDownloading
          }
        });
        console.error('Error downloading PDF:', error);
        alert('Failed to download PDF. Please try again.');
      } finally {
        setIsDownloading(false);
      }
    }

    /**
     * Open review window
     * @param {any} reportData - The report data for review
     * @param {string} reportId - The report ID
     */
    function openReviewWindow(reportData, reportId) {
      mixpanel.track("Request Review Report", {
        reportId: reportId,
        patientName: reportData?.patient?.name || reportData?.patientInfo?.name
      })
      reviewWindowRef.current?.openWindow({
        ...reportData,
        _id: reportId,
      });
    }

    // Resize handler removed as it is now handled by SplitPane

    if (!isPanelOpen) return null

    return (
      <div
        className='diagnose-chat__report-section'
        data-test="chat-report-panel"
        style={{
          width: '100%',
          height: '100%',
          display: isPanelOpen ? 'block' : 'none',
          containerType: 'inline-size'
        }}
      >
        <div className="chat-report">
          {/* Resize Handle Removed */}

          <div className="chat-report__body">
            <div className="chat-report__header">
              {/* Back Button (Visible on both Mobile and Desktop now) */}
              <button
                type="button"
                className="chat-report__back-btn"
                title="Back"
                onClick={closePanel}
                data-test="report-panel-back-btn"
              >
                <IoArrowBack />
              </button>

              <h2 className="chat-report__title">Report preview</h2>

              <div className="chat-report__header-actions">
                {reportState === ReportState.PREVIEWING && reportData != null && (
                  <>
                    {/* Desktop Actions Removed - replaced by Ellipsis Menu below */}

                    {/* Actions (Ellipsis) - Always visible */}
                    <div className="chat-report__actions">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="chat-report__btn p-2 bg-transparent hover:bg-muted text-foreground"
                            data-variant="secondary"
                            style={{ backgroundColor: 'transparent', border: 'none' }}>
                            <IoEllipsisVertical className="h-5 w-5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="z-[1000]">
                          {report?._id != null && (
                            <DropdownMenuItem onClick={gotoEditForm} inset={false} className="">
                              <MdEdit className="mr-2 h-4 w-4" />
                              <span>Edit Report</span>
                            </DropdownMenuItem>
                          )}

                          {(reportData?.patient != null || reportData?.patientInfo != null) && (
                            <DropdownMenuItem
                              onClick={() => handleDownloadPDF(reportData, report)}
                              disabled={isDownloading}
                              inset={false}
                              className=""
                            >
                              {isDownloading ? (
                                <div className="chat-report__btn-spinner mr-2"></div>
                              ) : (
                                <IoDocumentText className="mr-2 h-4 w-4" />
                              )}
                              <span>{isDownloading ? 'Downloading...' : 'Download PDF'}</span>
                            </DropdownMenuItem>
                          )}

                          {report?._id != null && (
                            <>
                              <DropdownMenuItem onClick={openSharingWindow} inset={false} className="">
                                <IoMdShareAlt className="mr-2 h-4 w-4" />
                                <span>Share</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openReviewWindow(reportData, report?._id)} inset={false} className="">
                                <FaSync className="mr-2 h-4 w-4" />
                                <span>Request review</span>
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </>
                )}

                {/* Desktop Close Button Removed - replaced by Back Button on left */}
              </div>
            </div>


            {/* Scrollable content area */}
            <div className="chat-report__content">
              {reportState === ReportState.LOADING && reportData != null && (
                <ReportLoading
                  loadingText="Generating report..."
                  reportData={reportData}
                  report={report}
                />
              )}
              {reportState === ReportState.EDITING && reportData != null && (
                <EditReport
                  reportId={report?._id}
                  reportData={reportData}
                  gotoPreview={gotoPreview}
                />
              )}
              {reportState === ReportState.PREVIEWING && reportData != null && (
                <PreviewReport
                  reportData={reportData}
                />
              )}
            </div>

            <DocumentSharingWindow ref={sharingWindowRef} />
            <ReviewRequestWindow ref={reviewWindowRef} />
          </div>
        </div>
      </div>
    )
  }
)

/**
 * Custom hook to handle report data logic for a specific chat session
 * @param {string} activeChatSessionId - ID of the active chat session
 * @param {ReportSource} reportSource - Either { type: 'ref', ref: string } or { type: 'report', report: ChatReport }
 * @param {Array<ChatReport>} activeChatSessionReports - Array of reports from chat context
 * @returns {{ report: ChatReport | null, reportData: ReportData | null, isStreamingReport: boolean }} Report data and state
 */
function useReportData(activeChatSessionId, reportSource, activeChatSessionReports) {
  const { useCancelActiveChatStream, useStreamingReport } = useChatContext()

  const isStreamingReport = reportSource?.type === "stream"
  const streamingReport = useStreamingReport(activeChatSessionId);
  const { mutate: cancelStream } = useCancelActiveChatStream()
  const report = useMemo(() => {
    if (!reportSource) return null

    if (reportSource.type === 'report') {
      return reportSource.report
    }

    if (reportSource.type === 'ref') {
      return activeChatSessionReports?.find(report => (
        report._id === reportSource.ref || report._clientId === reportSource.ref
      ))
    }

    if (reportSource.type === 'stream') {
      return streamingReport
    }

    return null
  }, [reportSource, activeChatSessionReports, streamingReport])

  const reportData = useMemo(() => {
    if (report == null) return null

    try {
      const rawReportJson = report.current
      const reportFields = parseJsonToFields(rawReportJson)

      return {
        patient: report.patientInfo,
        facility: report.facilityInfo,
        modality: getModalityLabel(report.modality),
        technique: report.technique,
        examinationReason: report.examinationReason,
        clinicalHistory: report.clinicalHistory,
        date: report.date,
        content: reportFields,
        rawReportJson: rawReportJson
      }
    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          context: 'ChatReportPanel - useReportData parseJsonToFields',
          reportId: report?._id || 'undefined',
          reportClientId: report?._clientId || 'undefined',
          activeChatSessionId: activeChatSessionId || 'undefined',
          reportChunksLength: report?.chunks?.length || 0,
          reportCurrentLength: report?.current?.length || 0,
          reportModality: report?.modality || 'undefined'
        }
      });
      console.log(err)
      showErrorMessage("Invalid report format")
      if (isStreamingReport) cancelStream()
      return null
    }
  }, [report, activeChatSessionId])

  return { report, reportData }
}

/**
 * @param {Object} props
 * @param {string} [props.loadingText]
 * @param {any} props.reportData
 * @param {any} [props.report]
 */
function ReportLoading({ loadingText = "Loading...", reportData, report }) {
  if (reportData == null) return null

  // Get the latest stream status or all statuses
  const streamStatuses = report?.streamStatuses || []
  const latestStatus = streamStatuses.length > 0 ? streamStatuses[streamStatuses.length - 1] : null

  return (
    <div className="chat-report__loading">
      <div className="chat-report__loading-header">
        <div className="chat-report__loader"></div>
        <p className="chat-report__loader-text">{loadingText}</p>
      </div>

      {/* Display the latest stream status */}
      {latestStatus && (
        <div className="chat-report__stream-status">
          <p className="chat-report__stream-status-text">
            <span className="chat-report__stream-status-label">Thinking:</span> {latestStatus}
          </p>
        </div>
      )}

      <div>
        <ChatMarkdown>{reportData.rawReportJson ? parseJsonToMarkdown(reportData.rawReportJson) : parseFieldsToMarkdown(reportData.content)}</ChatMarkdown>
      </div>
    </div>
  )
}

/**
 * @typedef {Object} ReportData
 * @property {ChatReport["patientInfo"]} patient
 * @property {ChatReport["patientInfo"]} [patientInfo] - For agent-generated reports
 * @property {ChatReport["facilityInfo"]} facility
 * @property {string} modality
 * @property {string} [technique]
 * @property {string} [examinationReason]
 * @property {string} [clinicalHistory]
 * @property {string} date
 * @property {ReportField[]} content
 * @property {string} [rawReportJson] - Raw JSON string for markdown parsing
 */

/**
 * @typedef {Object} ReportField
 * @property {string} label
 * @property {ReportFieldValue} value
 * @property {boolean=} required
 */

// TODO:  possibly add in panel editor
/**
 * @typedef {string | string[] | null | Record<string, string | string[] | null | Record<string, string | string[] | null>>} ReportFieldValue
 */
